import Breadcrumbs from "@/components/design-system/breadcrumbs";
import { Button } from "@/components/design-system/button";
import FilterWithRadio from "@/components/design-system/FilterWithRadio";
import Assignments from "@/modules/Course-Management/components/Assignments";
import { getAllStudentV2Service } from "@/service/student.service";
import { selectUser, selectUserRole } from "@/store/slices/authSlice";
import { ROLES } from "@/utils/constants";
import { ChevronLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";
import { classApi } from "../api/classApi";

type ClassDetailLocationState = {
  selectedStudentId?: string;
  assignmentType?: string;
  lessonId?: string | null;
  unitId?: string | null;
};

export default function ClassOfferingDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const user = useSelector(selectUser);
  const userRole = useSelector(selectUserRole);
  const locationState = (location.state as ClassDetailLocationState | null) ?? {};

  const [className, setClassName] = useState("");
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState<{ user_id: string; name: string }[]>(
    []
  );
  const [selectedStudentId, setSelectedStudentId] = useState(
    locationState.selectedStudentId ?? ""
  );
  const [selectedAssignmentType, setSelectedAssignmentType] = useState(
    locationState.assignmentType ?? "general"
  );

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    classApi
      .getClassById(id)
      .then((record) => setClassName(record?.name ?? ""))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!id || userRole?.code !== ROLES.TEACHER) {
      return;
    }

    void getAllStudentV2Service({
      page: 1,
      pageSize: 500,
      type: "class",
      classId: id,
      teacherId: user?.teacher_id,
      schoolId: user?.school_id,
    })
      .then(({ data }) => {
        const rows = (data.data ?? []).map((student) => ({
          user_id: student.user_id ?? student.id,
          name: student.name,
        }));
        setStudents(rows);

        if (locationState.selectedStudentId) {
          setSelectedStudentId(locationState.selectedStudentId);
        } else if (rows[0]) {
          setSelectedStudentId(rows[0].user_id);
        }
      })
      .catch((error) => {
        console.error("Error fetching class students:", error);
      });
  }, [
    id,
    locationState.selectedStudentId,
    user?.school_id,
    user?.teacher_id,
    userRole?.code,
  ]);

  if (!id) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-[16px] font-medium text-[#1F1F1F]">Loading…</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-[68px] items-center gap-3 px-[32px]">
        <Button
          type="button"
          variant="light"
          size="md"
          className="h-[40px] w-[40px] rounded-[8px] border border-[#EDEDED] bg-white p-0"
          onClick={() => navigate(-1)}
          leftIcon={<ChevronLeft className="h-5 w-5" />}
        >
          <span className="sr-only">Back</span>
        </Button>
        <Breadcrumbs
          title={className || "Class"}
          onClick={() => navigate(-1)}
          bg="#fff"
        />
      </div>

      <div className="px-[32px] pb-[24px]">
        <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-semibold text-[#2E2E32]">Assignments</h1>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <FilterWithRadio
              showAbove={false}
              options={[
                { value: "general", label: "General Assignment" },
                { value: "unit", label: "Unit Assignment" },
              ]}
              popupWidth="250px"
              value={selectedAssignmentType}
              className="w-full sm:w-[250px]"
              onChange={setSelectedAssignmentType}
            />
            {userRole?.code === ROLES.TEACHER && students.length > 0 ? (
              <FilterWithRadio
                showAbove={false}
                options={students.map((student) => ({
                  value: student.user_id,
                  label: student.name,
                }))}
                value={selectedStudentId}
                className="w-full sm:w-[235px]"
                onChange={setSelectedStudentId}
              />
            ) : null}
          </div>
        </div>

        <Assignments
          recordType="class"
          selectedAssignmentType={selectedAssignmentType}
          selectedStudentId={selectedStudentId}
          selectedLessonId={locationState.lessonId ?? ""}
          selectedUnitId={locationState.unitId ?? ""}
        />
      </div>
    </div>
  );
}
